/* =========================================================================
   LIFT Interactive Shader Wallpapers
   Five WebGL fragment-shader backdrops that react to mouse position + clicks.
   Palette is LIFT's: aloe / pistachio greens, shopify green, cat blue/purple/
   magenta, pixel pink/gold — all over the cinematic black canvas.
   API:  const fx = LIFTShaders.mount(canvas, { shader:'aurora', intensity:1 });
         fx.setShader('ripple');  fx.setIntensity(0.6);  fx.destroy();
   ========================================================================= */
window.LIFTShaders = (function () {
  'use strict';

  const ORDER  = ['aurora', 'metaballs', 'voronoi', 'ripple', 'flow'];
  const LABELS  = { aurora: 'Aurora', metaballs: 'Líquido', voronoi: 'Celdas', ripple: 'Ondas', flow: 'Flujo' };
  const BLURB  = {
    aurora:    'Bandas de aurora que se deforman hacia el cursor. Haz clic para enviar un pulso.',
    metaballs: 'Gotas líquidas que se fusionan; una persigue el cursor. Clic = nueva gota.',
    voronoi:   'Celdas geométricas que se encienden cerca del cursor. Clic = onda expansiva.',
    ripple:    'Interferencia de ondas concéntricas desde el cursor. Clic = anillo que viaja.',
    flow:      'Campo de flujo con vórtice bajo el cursor. Clic = remolino momentáneo.'
  };

  const VERT = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';

  /* ---- shared GLSL prelude: uniforms, LIFT palette, noise helpers ---- */
  const PRELUDE = `
  precision highp float;
  uniform vec2  u_res;
  uniform float u_time;
  uniform vec2  u_mouse;     // 0..1
  uniform vec2  u_mvel;      // smoothed velocity
  uniform float u_intensity;
  uniform vec3  u_clicks[8]; // xy = pos(0..1), z = age(sec); z < 0 => inactive

  const vec3 ALOE = vec3(0.757, 0.984, 0.831); // #c1fbd4
  const vec3 PIST = vec3(0.831, 0.976, 0.878); // #d4f9e0
  const vec3 PGRN = vec3(0.584, 0.749, 0.278); // #95BF47
  const vec3 PGDK = vec3(0.369, 0.557, 0.243); // #5E8E3E
  const vec3 BLUE = vec3(0.165, 0.435, 0.859); // #2A6FDB
  const vec3 PURP = vec3(0.420, 0.310, 0.878); // #6B4FE0
  const vec3 MAG  = vec3(0.753, 0.216, 0.604); // #C0379A
  const vec3 PINK = vec3(0.910, 0.435, 0.627); // #E86FA0
  const vec3 GOLD = vec3(0.961, 0.760, 0.294); // #F5C24B

  float hash21(vec2 p){ p = fract(p*vec2(123.34,345.45)); p += dot(p, p+34.345); return fract(p.x*p.y); }
  vec2  hash22(vec2 p){ float n = hash21(p); return vec2(n, hash21(p+n)); }
  float vnoise(vec2 p){
    vec2 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f);
    float a=hash21(i), b=hash21(i+vec2(1,0)), c=hash21(i+vec2(0,1)), d=hash21(i+vec2(1,1));
    return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
  }
  float fbm(vec2 p){
    float s=0.0, a=0.5; mat2 r=mat2(0.8,0.6,-0.6,0.8);
    for(int i=0;i<5;i++){ s+=a*vnoise(p); p=r*p*2.0+10.0; a*=0.5; }
    return s;
  }
  vec2 toP(vec2 n){ vec2 q=(n-0.5); q.x*=u_res.x/u_res.y; return q; } // 0..1 -> aspect space
  `;

  const MAIN = `
  void main(){
    vec2 uv = gl_FragCoord.xy / u_res;
    vec2 p  = (uv - 0.5); p.x *= u_res.x/u_res.y;
    vec2 mp = toP(u_mouse);
    vec3 col = scene(p, mp);
    col *= u_intensity;
    col = max(col, 0.0);
    col = col / (1.0 + col*0.35);          // gentle filmic roll-off
    gl_FragColor = vec4(col, 1.0);
  }
  `;

  /* ---- 1 · AURORA ---- */
  const AURORA = `
  vec3 scene(vec2 p, vec2 mp){
    float t = u_time*0.06;
    vec2 q = p*1.7;
    vec2 warp = vec2(fbm(q + vec2(0.0, t)), fbm(q + vec2(5.2, -t)));
    warp += 0.5*(mp - p);                       // bend the field toward the cursor
    float n  = fbm(q + warp*1.25 + t);
    float bd = fbm(vec2(p.x*1.1 + n*1.6, p.y*2.6 - t*2.0));
    vec3 col = mix(PGDK*0.6, ALOE, smoothstep(0.18,0.85,bd));
    col = mix(col, BLUE, smoothstep(0.55,1.0,n)*0.45);
    col = mix(col, PURP, smoothstep(0.62,1.0,bd*n)*0.4);
    col += ALOE * smoothstep(0.9,0.0,length(p-mp))*0.18;   // soft cursor glow
    col *= 0.55 * smoothstep(1.35,0.15,length(p));         // vignette
    for(int i=0;i<8;i++){
      vec3 c=u_clicks[i]; if(c.z<0.0) continue;
      vec2 cp=toP(c.xy); float d=length(p-cp);
      col += ALOE * smoothstep(0.10,0.0,abs(d - c.z*0.7)) * exp(-c.z*1.6) * 0.9;
    }
    return col;
  }`;

  /* ---- 2 · METABALLS ---- */
  const METABALLS = `
  uniform vec3 u_mb0;
  uniform vec3 u_mb1;
  uniform vec3 u_mb2;
  uniform vec3 u_mb3;
  uniform vec3 u_mbc;
  float blob(vec2 p, vec2 c){ return 0.045 / (0.012 + dot(p-c, p-c)); }
  vec3 scene(vec2 p, vec2 mp){
    float t = u_time*0.25;
    vec2 c0=vec2(sin(t*0.7)*0.42, cos(t*0.6)*0.28);
    vec2 c1=vec2(cos(t*0.5)*0.46, sin(t*0.8)*0.30);
    vec2 c2=vec2(sin(t*0.9+2.0)*0.34, cos(t*0.4+1.0)*0.36);
    float w0=blob(p,c0), w1=blob(p,c1), w2=blob(p,c2), w3=blob(p,mp)*1.25;
    vec3 ccol=vec3(0.0); float wc=0.0;
    for(int i=0;i<8;i++){
      vec3 c=u_clicks[i]; if(c.z<0.0) continue;
      vec2 cp=toP(c.xy); float w=blob(p,cp)*(0.6+c.z*0.8)*exp(-c.z*0.9);
      wc+=w; ccol+=u_mbc*w;
    }
    float W = w0+w1+w2+w3+wc;
    vec3 col = (w0*u_mb0 + w1*u_mb1 + w2*u_mb2 + w3*u_mb3 + ccol) / max(W,0.001);
    col *= smoothstep(0.75, 2.1, W);
    col += u_mb0 * smoothstep(0.35,0.0,abs(W-1.0))*0.10;
    return col*0.95;
  }`;

  /* ---- 3 · VORONOI ---- */
  const VORONOI = `
  vec3 scene(vec2 p, vec2 mp){
    float t = u_time*0.2;
    vec2 g = (p+0.5)*5.0;
    vec2 ip=floor(g), fp=fract(g);
    float f1=8.0, f2=8.0; vec2 cell=vec2(0.0);
    for(int y=-1;y<=1;y++) for(int x=-1;x<=1;x++){
      vec2 o=vec2(float(x),float(y));
      vec2 pt=o + 0.5 + 0.5*sin(t + 6.2831*hash22(ip+o));
      float d=length(fp-pt);
      if(d<f1){ f2=f1; f1=d; cell=ip+o; } else if(d<f2){ f2=d; }
    }
    float edge = smoothstep(0.0, 0.06, f2-f1);        // 0 on borders
    float h = hash21(cell);
    vec3 base = h<0.4 ? PGDK : (h<0.7 ? BLUE : PURP);
    base = mix(base, ALOE, step(0.92,h));
    // cursor proximity lights cells
    vec2 cc = (cell+0.5)/5.0 - 0.5; cc.x = cc.x; // approx cell center back in p-space
    vec2 cellCenter = (cell + 0.5)/5.0 - 0.5; cellCenter.x *= 1.0;
    float prox = smoothstep(0.6, 0.0, length(p - mp));
    vec3 col = base * (0.12 + 0.5*prox);
    col += ALOE * (1.0-edge) * (0.10 + 0.25*prox);    // glowing borders
    // click wave lights cells by distance ring
    for(int i=0;i<8;i++){
      vec3 c=u_clicks[i]; if(c.z<0.0) continue;
      vec2 cp=toP(c.xy); float d=length(p-cp);
      col += base * smoothstep(0.18,0.0,abs(d - c.z*0.8)) * exp(-c.z*1.4) * 1.4;
    }
    return col;
  }`;

  /* ---- 4 · RIPPLE / INTERFERENCE ---- */
  const RIPPLE = `
  vec3 scene(vec2 p, vec2 mp){
    float t=u_time;
    float w=0.0;
    float dm=length(p-mp);
    w += sin(dm*42.0 - t*3.0) * exp(-dm*2.2);                       // cursor emitter
    w += 0.5*sin(length(p-vec2( 0.32, 0.18))*30.0 - t*2.0);         // ambient
    w += 0.5*sin(length(p+vec2( 0.30, 0.10))*26.0 + t*1.7);
    for(int i=0;i<8;i++){
      vec3 c=u_clicks[i]; if(c.z<0.0) continue;
      vec2 cp=toP(c.xy); float d=length(p-cp);
      w += sin(d*34.0 - c.z*9.0) * exp(-c.z*1.1) * exp(-d*1.4) * 1.6;
    }
    float v = w*0.5 + 0.5;
    vec3 col = mix(vec3(0.0), ALOE, smoothstep(0.46,0.78,v));
    col = mix(col, BLUE, smoothstep(0.78,0.96,v)*0.6);
    col += ALOE * smoothstep(0.16,0.0,dm)*0.25;                     // cursor core
    col *= 0.75 * smoothstep(1.4,0.1,length(p));
    return col;
  }`;

  /* ---- 5 · FLOW FIELD ---- */
  const FLOW = `
  vec3 scene(vec2 p, vec2 mp){
    float t=u_time*0.1;
    float a = fbm(p*1.6 + t) * 6.2831;
    vec2 dir = vec2(cos(a), sin(a));
    vec2 d = p - mp; float r = length(d);
    dir += vec2(-d.y, d.x)/(r+0.18) * 0.6 * exp(-r*1.6);            // swirl at cursor
    for(int i=0;i<8;i++){
      vec3 c=u_clicks[i]; if(c.z<0.0) continue;
      vec2 cp=toP(c.xy); vec2 dd=p-cp; float rr=length(dd);
      dir += vec2(-dd.y, dd.x)/(rr+0.12) * exp(-c.z*1.3) * exp(-rr*2.0) * 1.6;
    }
    float s1 = fbm(p*3.0 - dir*0.9 + t*2.0);
    float s2 = fbm(vec2(s1*2.2, dot(p,dir)*4.5) - t*1.4);
    float s  = smoothstep(0.4,0.72,s2);
    vec3 col = mix(PGDK*0.7, ALOE, s);
    col = mix(col, PURP, smoothstep(0.6,0.92,s2)*0.5);
    col = mix(col, BLUE, smoothstep(0.55,0.85,s1)*0.3);
    col *= 0.62 * smoothstep(1.3,0.12,length(p));
    return col;
  }`;

  const BODIES = { aurora: AURORA, metaballs: METABALLS, voronoi: VORONOI, ripple: RIPPLE, flow: FLOW };

  function compile(gl, type, src){
    const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.error(gl.getShaderInfoLog(s), src); return null; }
    return s;
  }
  function program(gl, fragBody){
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, PRELUDE + fragBody + MAIN);
    if (!vs || !fs) return null;
    const pr = gl.createProgram(); gl.attachShader(pr, vs); gl.attachShader(pr, fs); gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { console.error(gl.getProgramInfoLog(pr)); return null; }
    return pr;
  }

  function hexToRGB(hex) {
    if (!hex || typeof hex !== 'string' || hex.charAt(0) !== '#' || hex.length < 7) return null;
    return [parseInt(hex.slice(1,3),16)/255, parseInt(hex.slice(3,5),16)/255, parseInt(hex.slice(5,7),16)/255];
  }

  function mount(canvas, opts){
    opts = opts || {};
    const c = opts.colors || {};
    const mbC0 = hexToRGB(c.c0) || [0.757,0.984,0.831]; // ALOE
    const mbC1 = hexToRGB(c.c1) || [0.165,0.435,0.859]; // BLUE
    const mbC2 = hexToRGB(c.c2) || [0.420,0.310,0.878]; // PURP
    const mbC3 = hexToRGB(c.c3) || [0.584,0.749,0.278]; // PGRN
    const mbCC = hexToRGB(c.cc) || [0.910,0.435,0.627]; // PINK
    const gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false, alpha: false });
    if (!gl) { console.warn('WebGL unavailable'); return { setShader(){}, setIntensity(){}, destroy(){} }; }

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);

    const progs = {}; ORDER.forEach(n => { progs[n] = program(gl, BODIES[n]); });
    let cur = opts.shader && progs[opts.shader] ? opts.shader : ORDER[0];
    let intensity = opts.intensity == null ? 1.0 : opts.intensity;
    const target = opts.pointerTarget || window;

    // pointer state
    const mouse = { x: 0.5, y: 0.5 }, sm = { x: 0.5, y: 0.5 }, prev = { x: 0.5, y: 0.5 }, vel = { x: 0, y: 0 };
    const clicks = []; // {x,y,t0}
    function rel(e){ const r = canvas.getBoundingClientRect(); return { x:(e.clientX-r.left)/r.width, y:1-(e.clientY-r.top)/r.height }; }
    function onMove(e){ const m = rel(e); mouse.x = m.x; mouse.y = m.y; }
    function onDown(e){ const m = rel(e); clicks.push({ x:m.x, y:m.y, t0: performance.now()/1000 }); if (clicks.length>8) clicks.shift(); }
    target.addEventListener('pointermove', onMove, { passive: true });
    target.addEventListener('pointerdown', onDown, { passive: true });

    let raf, t0 = performance.now(), running = true;
    function resize(){
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      const w = canvas.clientWidth || canvas.parentElement.clientWidth || 800;
      const h = canvas.clientHeight || canvas.parentElement.clientHeight || 600;
      const W = Math.max(1, Math.round(w*dpr)), H = Math.max(1, Math.round(h*dpr));
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
    }
    const ro = new ResizeObserver(resize); ro.observe(canvas);
    resize();

    const clickBuf = new Float32Array(24);
    function frame(now){
      if (!running) return;
      const t = (now - t0)/1000;
      // smooth mouse + velocity
      sm.x += (mouse.x - sm.x)*0.08; sm.y += (mouse.y - sm.y)*0.08;
      vel.x = (sm.x - prev.x); vel.y = (sm.y - prev.y); prev.x = sm.x; prev.y = sm.y;
      for (let i=0;i<8;i++){
        const c = clicks[clicks.length-8+i];
        if (c) { const age = now/1000 - c.t0; clickBuf[i*3]=c.x; clickBuf[i*3+1]=c.y; clickBuf[i*3+2]= age>3.0?-1:age; }
        else { clickBuf[i*3]=0; clickBuf[i*3+1]=0; clickBuf[i*3+2]=-1; }
      }
      gl.viewport(0,0,canvas.width,canvas.height);
      const pr = progs[cur]; gl.useProgram(pr);
      const loc = n => gl.getUniformLocation(pr, n);
      const ap = gl.getAttribLocation(pr,'p');
      gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.enableVertexAttribArray(ap); gl.vertexAttribPointer(ap,2,gl.FLOAT,false,0,0);
      gl.uniform2f(loc('u_res'), canvas.width, canvas.height);
      gl.uniform1f(loc('u_time'), t);
      gl.uniform2f(loc('u_mouse'), sm.x, sm.y);
      gl.uniform2f(loc('u_mvel'), vel.x, vel.y);
      gl.uniform1f(loc('u_intensity'), intensity);
      gl.uniform3fv(loc('u_clicks'), clickBuf);
      gl.uniform3fv(loc('u_mb0'), mbC0);
      gl.uniform3fv(loc('u_mb1'), mbC1);
      gl.uniform3fv(loc('u_mb2'), mbC2);
      gl.uniform3fv(loc('u_mb3'), mbC3);
      gl.uniform3fv(loc('u_mbc'), mbCC);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return {
      setShader(n){ if (progs[n]) cur = n; },
      getShader(){ return cur; },
      setIntensity(v){ intensity = v; },
      pulse(){ clicks.push({ x:sm.x, y:sm.y, t0: performance.now()/1000 }); if (clicks.length>8) clicks.shift(); },
      destroy(){ running=false; cancelAnimationFrame(raf); ro.disconnect(); target.removeEventListener('pointermove',onMove); target.removeEventListener('pointerdown',onDown); }
    };
  }

  return { mount, ORDER, LABELS, BLURB };
})();
